import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, Sankey } from 'recharts';
import type { LinkProps, NodeProps } from 'recharts/types/chart/Sankey';
import type { StrategistTransition } from '@/entities/strategist';
import { formatLiftPp, liftColorClass, RELIABLE_THRESHOLD } from './lib';
import { SankeyLinkShape } from './SankeyLinkShape';
import { SankeyNodeShape } from './SankeyNodeShape';
import { buildSankeyGraph, maxColumnSize } from './sankeyData';

type Props = {
  /** Уже отсортированные и ограниченные топ-N переходы */
  transitions: StrategistTransition[];
  /** Изолированное понятие, либо null */
  selectedNodeId: string | null;
  /** Нажатие на узел — изолировать понятие либо снять изоляцию */
  onSelectNode: (id: string | null) => void;
  /** Нажатие на узел — открыть карточку понятия */
  onConceptClick: (id: string) => void;
  /** Нажатие на ленту — провалиться в диалоги перехода */
  onTransitionClick: (transition: StrategistTransition) => void;
};

/** Ширина столбика узла и просвет между узлами колонки в единицах диаграммы. */
const NODE_WIDTH = 12;
const NODE_PADDING = 14;

/** Полоса под подписи понятий по краям диаграммы. */
const LABEL_WIDTH = 272;

/** Высота, отводимая одному узлу колонки, и наименьшая высота всей диаграммы. */
const NODE_SLOT_HEIGHT = 44;
const MIN_HEIGHT = 320;

/**
 * Диаграмма связей «возражение → аргумент»: слева возражения клиента, справа аргументы
 * оператора, толщина ленты — число диалогов перехода, цвет — знак влияния на целевой исход.
 * Нажатие на понятие изолирует его переходы, нажатие на ленту открывает её диалоги.
 */
export const TransitionsSankey: FC<Props> = ({
  transitions,
  selectedNodeId,
  onSelectNode,
  onConceptClick,
  onTransitionClick,
}) => {
  const { t } = useTranslation('strategist');
  // Лента под курсором — подсвечивается ярче остальных.
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const graph = useMemo(() => buildSankeyGraph(transitions), [transitions]);
  const height = Math.max(MIN_HEIGHT, maxColumnSize(graph.nodes) * NODE_SLOT_HEIGHT);

  const handleNodeClick = (conceptId: string) => {
    // Нажатие на узел изолирует его переходы и открывает карточку понятия.
    onSelectNode(selectedNodeId === conceptId ? null : conceptId);
    onConceptClick(conceptId);
  };

  const renderNode = (props: NodeProps) => {
    const node = graph.nodes[props.index];
    return (
      <SankeyNodeShape
        x={props.x}
        y={props.y}
        width={props.width}
        height={props.height}
        labelWidth={LABEL_WIDTH}
        node={node}
        isDimmed={selectedNodeId !== null && node.conceptId !== selectedNodeId}
        onClick={() => handleNodeClick(node.conceptId)}
      />
    );
  };

  const renderLink = (props: LinkProps) => {
    const { transition } = graph.links[props.index];
    const source = graph.nodes[graph.links[props.index].source];
    const target = graph.nodes[graph.links[props.index].target];
    const isDimmed =
      selectedNodeId !== null && source.conceptId !== selectedNodeId && target.conceptId !== selectedNodeId;
    return (
      <SankeyLinkShape
        sourceX={props.sourceX}
        sourceY={props.sourceY}
        sourceControlX={props.sourceControlX}
        targetX={props.targetX}
        targetY={props.targetY}
        targetControlX={props.targetControlX}
        linkWidth={props.linkWidth}
        // Ненадёжный переход не окрашивается по знаку влияния — на таком объёме знак случаен.
        colorClass={transition.n >= RELIABLE_THRESHOLD ? liftColorClass(transition.lift) : 'text-muted-foreground'}
        isDimmed={isDimmed}
        isHovered={hoveredIndex === props.index}
        tooltip={t('transitions.sankey.linkTooltip', {
          from: source.name,
          to: target.name,
          n: transition.n,
          lift: formatLiftPp(transition.lift),
        })}
        onMouseEnter={() => setHoveredIndex(props.index)}
        onMouseLeave={() => setHoveredIndex((prev) => (prev === props.index ? null : prev))}
        onClick={() => onTransitionClick(transition)}
      />
    );
  };

  return (
    <div className="w-full overflow-x-auto" role="img" aria-label={t('transitions.sankey.aria')}>
      <div className="min-w-225" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <Sankey
            data={graph}
            nodeWidth={NODE_WIDTH}
            nodePadding={NODE_PADDING}
            margin={{ top: 12, right: LABEL_WIDTH, bottom: 12, left: LABEL_WIDTH }}
            node={renderNode}
            link={renderLink}
          />
        </ResponsiveContainer>
      </div>
    </div>
  );
};
